import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { useState, type ReactNode } from "react";

import type { CriterionDetails, QuestionSourceLink, RequirementRenderOptions } from "./types";

import {
  evalDialogContentSx,
  evalDialogStackSx,
  formatEnumLabel,
  formatPercent,
  formatPoints,
  formatRequirementKey,
  formatRequirementScalar,
  formatStatusLabel,
  getAggregationRoleChipColor,
  getCategoryChipColor,
  getRequirementKeyDescription,
  getRequirementChipLabel,
  getScoreColor,
  hasDisplayValue,
  isPrimitiveRequirementValue,
  isRecord,
  resultEvidenceHiddenKeys,
  shouldShowAggregationRole,
  titleFromKey,
} from "./scoutEvalUtils";

export function ScoreChip({ score }: { score: number | null | undefined }) {
  if (score === null || score === undefined) {
    return <Chip label="n/a" size="small" variant="outlined" />;
  }

  return (
    <Chip
      label={formatPercent(score)}
      color={getScoreColor(score)}
      size="small"
      variant="outlined"
    />
  );
}

export function StatusChip({ status }: { status: string }) {
  if (status === "SUCCEEDED") {
    return <Chip label="Succeeded" color="success" size="small" variant="outlined" />;
  }
  if (status === "FAILED") {
    return <Chip label="Failed" color="error" size="small" variant="outlined" />;
  }
  if (status === "RUNNING") {
    return <Chip label="Running" color="warning" size="small" variant="outlined" />;
  }
  return <Chip label={formatStatusLabel(status)} size="small" variant="outlined" />;
}

export function QuestionScoreOutcomeChip({
  status,
  score,
}: {
  status: string;
  score: number | null | undefined;
}) {
  if (status !== "SUCCEEDED") {
    return <StatusChip status={status} />;
  }
  if (score === null || score === undefined) {
    return <Chip label="Not scored" size="small" variant="outlined" />;
  }
  if (score <= 0) {
    return <Chip label="Failed" color="error" size="small" variant="outlined" />;
  }
  if (score >= 100) {
    return <Chip label="Full score" color="success" size="small" variant="outlined" />;
  }
  return <Chip label="Partial score" color="warning" size="small" variant="outlined" />;
}

export function QuestionSourceLinks({ sourceLinks }: { sourceLinks: QuestionSourceLink[] }) {
  if (sourceLinks.length === 0) {
    return null;
  }

  return (
    <Stack spacing={0.75}>
      <Typography variant="caption" color="text.secondary">
        Sources
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {sourceLinks.map((sourceLink) => (
          <Button
            key={`${sourceLink.label}-${sourceLink.url}`}
            href={sourceLink.url}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon fontSize="small" />}
            sx={{
              justifyContent: "flex-start",
              maxWidth: "100%",
              textAlign: "left",
              textTransform: "none",
              whiteSpace: "normal",
            }}
          >
            {sourceLink.label}
          </Button>
        ))}
      </Stack>
    </Stack>
  );
}

function readStringField(value: Record<string, unknown>, key: string) {
  const field = value[key];
  return typeof field === "string" && field.trim().length > 0 ? field : null;
}

function ObservedResourcesTable({ resources }: { resources: unknown[] }) {
  const rows = resources.filter(isRecord);

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        None
      </Typography>
    );
  }

  return (
    <TableContainer
      sx={{
        mt: 0.5,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        maxHeight: 320,
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell>URL</TableCell>
            <TableCell>Source ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((resource, index) => {
            const title = readStringField(resource, "title");
            const url = readStringField(resource, "url");
            const sourceId = readStringField(resource, "sourceId");
            const id = readStringField(resource, "id");

            return (
              <TableRow key={id ?? `${title ?? "resource"}-${index}`}>
                <TableCell>
                  <Typography variant="body2">{title ?? "-"}</Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  {url ? (
                    <MuiLink
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {url}
                    </MuiLink>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{sourceId ?? "-"}</Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RequirementLabel({
  requirementKey,
  variant = "caption",
}: {
  requirementKey: string;
  variant?: "caption" | "subtitle2";
}) {
  const label = formatRequirementKey(requirementKey);
  const description = getRequirementKeyDescription(requirementKey);

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" component="span">
      <Typography variant={variant} color={variant === "caption" ? "text.secondary" : undefined}>
        {label}
      </Typography>
      {description ? (
        <Tooltip title={description} arrow placement="right">
          <Box
            component="span"
            aria-label={`${label} information`}
            tabIndex={0}
            sx={{
              color: "text.secondary",
              cursor: "help",
              display: "inline-flex",
              lineHeight: 0,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          </Box>
        </Tooltip>
      ) : null}
    </Stack>
  );
}

const scalarRequirementRowSx = {
  alignItems: "baseline",
  columnGap: 2,
  display: { xs: "block", sm: "grid" },
  gridTemplateColumns: "minmax(180px, 32%) minmax(0, 1fr)",
  py: 0.25,
};
const requirementGroupSx = {
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  p: 1.25,
};

function RequirementEntry({
  requirementKey,
  value,
  options,
}: {
  requirementKey: string;
  value: unknown;
  options?: RequirementRenderOptions;
}) {
  const hasNestedValue = !isPrimitiveRequirementValue(value);

  return (
    <Box sx={hasNestedValue ? undefined : scalarRequirementRowSx}>
      <RequirementLabel requirementKey={requirementKey} />
      <Box sx={hasNestedValue ? { mt: 0.5 } : undefined}>
        {requirementKey === "observedResources" && Array.isArray(value) ? (
          <ObservedResourcesTable resources={value} />
        ) : (
          <RequirementValue value={value} options={options} />
        )}
      </Box>
    </Box>
  );
}

function RequirementValue({
  value,
  options,
}: {
  value: unknown;
  options?: RequirementRenderOptions;
}) {
  if (isPrimitiveRequirementValue(value)) {
    return <Typography variant="body2">{formatRequirementScalar(value)}</Typography>;
  }

  if (Array.isArray(value)) {
    const displayItems = value.filter((item) => hasDisplayValue(item, options));

    if (displayItems.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          None
        </Typography>
      );
    }

    if (displayItems.every((item) => getRequirementChipLabel(item) !== null)) {
      return (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {displayItems.map((item, index) => (
            <Chip
              key={`${getRequirementChipLabel(item) ?? "item"}-${index}`}
              label={getRequirementChipLabel(item) ?? "Unsupported"}
              size="small"
              variant="outlined"
            />
          ))}
        </Stack>
      );
    }

    if (
      displayItems.every((group) => {
        if (!Array.isArray(group)) {
          return false;
        }
        return group
          .filter((item) => hasDisplayValue(item, options))
          .every((item) => getRequirementChipLabel(item) !== null);
      })
    ) {
      return (
        <Stack spacing={0.75}>
          {displayItems.map((group, index) => {
            if (!Array.isArray(group)) {
              return null;
            }
            const displayGroup = group.filter((item) => hasDisplayValue(item, options));

            return (
              <Box
                key={`requirement-group-${index}`}
                sx={{
                  alignItems: "center",
                  columnGap: 1,
                  display: { xs: "block", sm: "grid" },
                  gridTemplateColumns: "auto minmax(0, 1fr)",
                  rowGap: 0.5,
                }}
              >
                <Typography component="span" variant="caption" color="text.secondary">
                  Group {index + 1}
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {displayGroup.map((item, itemIndex) => (
                    <Chip
                      key={`${getRequirementChipLabel(item) ?? "item"}-${itemIndex}`}
                      label={getRequirementChipLabel(item) ?? "Unsupported"}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      );
    }

    return (
      <Stack spacing={1}>
        {displayItems.map((item, index) => (
          <Box key={`requirement-item-${index}`}>
            <Typography variant="caption" color="text.secondary">
              Item {index + 1}
            </Typography>
            <Box sx={!isPrimitiveRequirementValue(item) ? { mt: 0.5 } : undefined}>
              <RequirementValue value={item} options={options} />
            </Box>
          </Box>
        ))}
      </Stack>
    );
  }

  if (isRecord(value)) {
    const entries = Object.entries(value).filter(([key, nestedValue]) => {
      return (
        (key !== "scoreAggregationRole" ||
          (typeof nestedValue === "string" && shouldShowAggregationRole(nestedValue))) &&
        options?.hiddenKeys?.has(key) !== true &&
        !(options?.hideRequirements === true && key === "requirements") &&
        hasDisplayValue(nestedValue, options)
      );
    });
    if (entries.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          None
        </Typography>
      );
    }

    return (
      <Stack spacing={1}>
        {entries.map(([key, nestedValue]) => (
          <RequirementEntry key={key} requirementKey={key} value={nestedValue} options={options} />
        ))}
      </Stack>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      Unsupported requirement value
    </Typography>
  );
}

function RequirementsDisplay({
  value,
  options,
}: {
  value: unknown;
  options?: RequirementRenderOptions;
}) {
  if (value === null || value === undefined) {
    return (
      <Typography variant="body2" color="text.secondary">
        No structured requirements.
      </Typography>
    );
  }

  return <RequirementValue value={value} options={options} />;
}

function getFirstRecordItem(value: unknown) {
  if (!Array.isArray(value)) {
    return null;
  }
  return value.find(isRecord) ?? null;
}

function hasDisplayItems(value: unknown, options?: RequirementRenderOptions) {
  return hasDisplayValue(value, options);
}

function getCurrentToolEvidenceResult(resultEvidence: Record<string, unknown>) {
  const check = getFirstRecordItem(resultEvidence.currentToolEvidence);
  if (!check) {
    return null;
  }

  const evidence = isRecord(check.evidence) ? check.evidence : check;
  const result: Record<string, unknown> = {};

  if (typeof check.passed === "boolean") {
    result.status = check.passed ? "Satisfied" : "Failed";
  }
  if (hasDisplayItems(evidence.missingTools)) {
    result.missingTools = evidence.missingTools;
  }
  if (evidence.forbidWebFirst !== undefined) {
    result.forbidWebFirst = evidence.forbidWebFirst;
  }
  if (evidence.firstSuccessfulTool !== undefined) {
    result.firstSuccessfulTool = evidence.firstSuccessfulTool;
  }

  return hasDisplayValue(result) ? result : null;
}

function getRequirementResultEvidence(requirementKey: string, resultEvidence: unknown) {
  if (!isRecord(resultEvidence)) {
    return null;
  }

  if (requirementKey === "tools") {
    return hasDisplayItems(resultEvidence.missingTools)
      ? { missingTools: resultEvidence.missingTools }
      : "All required tools were observed.";
  }

  if (requirementKey === "anyToolFromEachGroup") {
    return hasDisplayItems(resultEvidence.missingAnyToolFromEachGroup)
      ? { missingAnyToolFromEachGroup: resultEvidence.missingAnyToolFromEachGroup }
      : "Every tool group was satisfied.";
  }

  if (requirementKey === "resources") {
    if (hasDisplayItems(resultEvidence.missingResources)) {
      return { missingResources: resultEvidence.missingResources };
    }
    if (hasDisplayItems(resultEvidence.observedResources)) {
      return { observedResources: resultEvidence.observedResources };
    }
    return "All required resources were found.";
  }

  if (requirementKey === "currentToolEvidence") {
    return getCurrentToolEvidenceResult(resultEvidence);
  }

  return null;
}

function RequirementGroupSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={700}
        sx={{ display: "block", mb: 0.25 }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

export function GroupedRequirementsDisplay({
  requirements,
  resultEvidence,
  resultOptions,
}: {
  requirements: unknown;
  resultEvidence?: unknown;
  resultOptions?: RequirementRenderOptions;
}) {
  if (!isRecord(requirements)) {
    return <RequirementsDisplay value={requirements} />;
  }

  const entries = Object.entries(requirements).filter(([key, value]) => {
    return (
      (key !== "scoreAggregationRole" ||
        (typeof value === "string" && shouldShowAggregationRole(value))) &&
      hasDisplayValue(value)
    );
  });
  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No structured requirements.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {entries.map(([key, value]) => {
        const result = getRequirementResultEvidence(key, resultEvidence);

        return (
          <Box key={key} sx={requirementGroupSx}>
            <RequirementLabel requirementKey={key} variant="subtitle2" />
            <Stack spacing={1} sx={{ mt: 1 }}>
              <RequirementGroupSection label="Requirement">
                <RequirementValue value={value} />
              </RequirementGroupSection>
              {hasDisplayValue(result, resultOptions) ? (
                <RequirementGroupSection label="Result">
                  <RequirementValue value={result} options={resultOptions} />
                </RequirementGroupSection>
              ) : null}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

function DetailField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box>{children}</Box>
    </Box>
  );
}

function DetailSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ borderTop: 1, borderColor: "divider", pt: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
        {label}
      </Typography>
      <Box>{children}</Box>
    </Box>
  );
}

function InlineCode({ children }: { children: ReactNode }) {
  return (
    <Box
      component="code"
      sx={{
        px: 0.5,
        py: 0.125,
        borderRadius: 0.5,
        bgcolor: "action.hover",
        fontFamily: "monospace",
        fontSize: "0.875em",
      }}
    >
      {children}
    </Box>
  );
}

function ExplainerList({ children }: { children: ReactNode }) {
  return (
    <Box component="ul" sx={{ pl: 2.5, mt: 1, mb: 0 }}>
      {children}
    </Box>
  );
}

function ExplainerListItem({ children }: { children: ReactNode }) {
  return (
    <Typography
      component="li"
      variant="body2"
      color="text.secondary"
      sx={{ mb: 0.75, "&:last-of-type": { mb: 0 } }}
    >
      {children}
    </Typography>
  );
}

function hasResourceRequirements(requirements: unknown) {
  if (!isRecord(requirements)) {
    return false;
  }

  const resources = requirements.resources;
  return Array.isArray(resources) && resources.some((resource) => hasDisplayValue(resource));
}

export function CriterionDetailsDialog({
  open,
  onClose,
  criterion,
}: {
  open: boolean;
  onClose: () => void;
  criterion: CriterionDetails;
}) {
  const hasCategory = hasDisplayValue(criterion.category);
  const hasScorerType = hasDisplayValue(criterion.scorerType);
  const hasScoreAggregationRole = shouldShowAggregationRole(criterion.scoreAggregationRole);
  const hasInstructions = hasDisplayValue(criterion.instructions);
  const hasRequirements = hasDisplayValue(criterion.requirements);
  const hasResultExplanation = hasDisplayValue(criterion.result?.explanation);
  const hiddenResultEvidenceKeys = hasResourceRequirements(criterion.requirements)
    ? resultEvidenceHiddenKeys
    : new Set([...resultEvidenceHiddenKeys, "observedResources"]);
  const resultEvidenceOptions: RequirementRenderOptions = {
    hiddenKeys: hiddenResultEvidenceKeys,
    hideRequirements: hasRequirements,
  };
  const hasResultEvidence = hasDisplayValue(criterion.result?.evidence, resultEvidenceOptions);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={titleFromKey(criterion.criterionKey)}
      maxWidth="md"
      fullWidth
      noPrimaryAction
      DialogContentProps={{ sx: evalDialogContentSx }}
      contentSx={evalDialogStackSx}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {hasCategory ? (
            <Chip
              label={formatEnumLabel(criterion.category)}
              color={getCategoryChipColor(criterion.category)}
              size="small"
              variant="outlined"
            />
          ) : null}
          {hasScorerType ? (
            <Chip
              label={formatEnumLabel(criterion.scorerType)}
              color="default"
              size="small"
              variant="outlined"
            />
          ) : null}
          {hasScoreAggregationRole ? (
            <Chip
              label={formatEnumLabel(criterion.scoreAggregationRole)}
              color={getAggregationRoleChipColor(criterion.scoreAggregationRole)}
              size="small"
              variant="outlined"
            />
          ) : null}
          <Chip
            label={`${formatPoints(criterion.maxPoints)} pts`}
            color="info"
            size="small"
            variant="outlined"
          />
          {criterion.enabled === false ? (
            <Chip label="Disabled" size="small" variant="outlined" />
          ) : null}
        </Stack>

        {criterion.result ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              label={criterion.result.passed ? "Pass" : "Fail"}
              color={criterion.result.passed ? "success" : "error"}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${formatPoints(criterion.result.pointsAwarded)} / ${formatPoints(
                criterion.maxPoints
              )} pts awarded`}
              color={criterion.result.passed ? "success" : "error"}
              size="small"
              variant="outlined"
            />
          </Stack>
        ) : null}

        {hasCategory || hasScorerType ? (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1.5, sm: 4 }}>
            {hasCategory ? (
              <DetailField label="Category">
                <Typography variant="body2">{formatEnumLabel(criterion.category)}</Typography>
              </DetailField>
            ) : null}
            {hasScorerType ? (
              <DetailField label="Scorer">
                <Typography variant="body2">{formatEnumLabel(criterion.scorerType)}</Typography>
              </DetailField>
            ) : null}
          </Stack>
        ) : null}
        {hasInstructions ? (
          <DetailSection label="Instructions">
            <Typography variant="body2">{criterion.instructions}</Typography>
          </DetailSection>
        ) : null}
        {criterion.result && hasResultExplanation ? (
          <DetailSection label="Result explanation">
            <Typography variant="body2">{criterion.result.explanation}</Typography>
          </DetailSection>
        ) : null}
        {hasRequirements ? (
          <DetailSection
            label={
              hasResultEvidence ? "Criteria requirements and results" : "Criteria requirements"
            }
          >
            <GroupedRequirementsDisplay
              requirements={criterion.requirements}
              resultEvidence={criterion.result?.evidence}
              resultOptions={resultEvidenceOptions}
            />
          </DetailSection>
        ) : null}
        {criterion.result ? (
          <>
            {!hasRequirements && hasResultEvidence ? (
              <DetailSection label="Result evidence">
                <RequirementsDisplay
                  value={criterion.result.evidence}
                  options={resultEvidenceOptions}
                />
              </DetailSection>
            ) : null}
          </>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function ScoutEvalExplainerButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<InfoOutlinedIcon />}
        onClick={() => setOpen(true)}
        sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
      >
        How evals work
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="About Scout Evals"
        maxWidth="md"
        fullWidth
        noPrimaryAction
        DialogContentProps={{ sx: evalDialogContentSx }}
        contentSx={evalDialogStackSx}
      >
        <Stack spacing={2}>
          <Typography variant="body2">
            Scout evals run benchmark questions through Scout, then score each answer against rubric
            criteria. Every question is designed around 100 criterion points.
          </Typography>
          <Box>
            <Typography variant="subtitle2">Deterministic criteria</Typography>
            <ExplainerList>
              <ExplainerListItem>
                Deterministic criteria evaluate persisted Scout evidence without an LLM judge.
              </ExplainerListItem>
              <ExplainerListItem>
                <InlineCode>tools</InlineCode> requires every listed tool to have a successful
                output.
              </ExplainerListItem>
              <ExplainerListItem>
                <InlineCode>anyToolFromEachGroup</InlineCode> uses grouped tool lists. Every group
                must be satisfied, and a group is satisfied when at least one tool in that group had
                a successful output.
              </ExplainerListItem>
              <ExplainerListItem>
                <InlineCode>resources</InlineCode> matches source URL or document parts by title,
                URL, or source ID. When a criterion uses resource requirements, the modal shows
                observed resource rows so reviewers can see what Scout sources the scorer
                considered; pass/fail still depends on whether any required resources are missing.
              </ExplainerListItem>
              <ExplainerListItem>
                <InlineCode>currentToolEvidence</InlineCode> looks at the successful tool calls
                saved from the Scout run. It is about how Scout gathered evidence, not whether the
                final answer text sounds correct.
              </ExplainerListItem>
              <ExplainerListItem>
                Inside <InlineCode>currentToolEvidence</InlineCode>, <InlineCode>tools</InlineCode>{" "}
                means required tools. If any listed tool is missing from the successful tool calls,
                the criterion fails.
              </ExplainerListItem>
              <ExplainerListItem>
                <InlineCode>forbidWebFirst</InlineCode> fails the criterion only when Scout's first
                successful evidence tool was <InlineCode>web_search</InlineCode>. We use this when
                the eval should prove Scout leaned on Encamp's custom compliance tools before
                searching the open web.
              </ExplainerListItem>
              <ExplainerListItem>
                Deterministic criteria do not evaluate final-answer text.
              </ExplainerListItem>
            </ExplainerList>
          </Box>
          <Box>
            <Typography variant="subtitle2">Group lists</Typography>
            <ExplainerList>
              <ExplainerListItem>
                The outer list is an all-of list: every group in the outer list must pass.
              </ExplainerListItem>
              <ExplainerListItem>
                Each inner group is an any-of list: any one item in that group can satisfy that
                single group.
              </ExplainerListItem>
              <ExplainerListItem>
                For <InlineCode>anyToolFromEachGroup</InlineCode>, each item is an acceptable tool
                name. The answer must show successful Scout evidence from at least one tool in each
                group.
              </ExplainerListItem>
              <ExplainerListItem>
                For <InlineCode>finalAnswer.includeAnyFromEachGroup</InlineCode>, each item is
                acceptable wording for one required fact. The final answer must include one
                acceptable phrase from each group.
              </ExplainerListItem>
            </ExplainerList>
          </Box>
          <Box>
            <Typography variant="subtitle2">LLM judge criteria</Typography>
            <ExplainerList>
              <ExplainerListItem>
                LLM judge criteria send the question, expected answer, transcript, criterion
                instructions, and requirements JSON to an LLM judge.
              </ExplainerListItem>
              <ExplainerListItem>
                The judge returns only pass/fail and an explanation.
              </ExplainerListItem>
              <ExplainerListItem>
                <InlineCode>finalAnswer.includeAnyFromEachGroup</InlineCode> checks semantic
                presence, so accepted wording does not need to be an exact string match.
              </ExplainerListItem>
              <ExplainerListItem>
                Other final-answer requirements compare jurisdiction sets, conditional labels, and
                category separation.
              </ExplainerListItem>
            </ExplainerList>
          </Box>
          <Box>
            <Typography variant="subtitle2">Scores</Typography>
            <ExplainerList>
              <ExplainerListItem>
                Each criterion awards either full points or zero points.
              </ExplainerListItem>
              <ExplainerListItem>
                A failed mandatory criterion forces the whole question score to zero.
              </ExplainerListItem>
              <ExplainerListItem>
                Otherwise, the question score is the sum of awarded criterion points.
              </ExplainerListItem>
              <ExplainerListItem>
                The final eval score is a weighted average across questions.
              </ExplainerListItem>
            </ExplainerList>
          </Box>
        </Stack>
      </Dialog>
    </>
  );
}

export function LoadingState() {
  return (
    <Stack py={8} alignItems="center">
      <CircularProgress />
    </Stack>
  );
}
